import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useTenantTranslation } from "hooks/formatters";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { MaterialChip } from "../components/MaterialChip";
import { useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { keyCodeToKeyName } from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { useDisableHotkeyScopes } from "components/scrapEyeLabelling/hooks/useDisableHotkeyScopes";
import { EDIT_EVENT_SUB_MODAL_DISABLED_SCOPES } from "../hotkeyScopes";
import { MaterialRead } from "src/store/api/generatedApi";

interface SelectMaterialModalProps {
  open: boolean;
  onClose: () => void;
  event: ScrapEyesEvent;
  filteredMaterials: MaterialRead[];
  onChange: (updates: Partial<ScrapEyesEvent>) => void;
  targetField: "material" | "coMaterial";
  excludeMaterialName?: string | null;
}

export const SelectMaterialModal = ({
  open,
  onClose,
  event,
  filteredMaterials,
  onChange,
  targetField,
  excludeMaterialName,
}: SelectMaterialModalProps) => {
  const { t } = useTenantTranslation();
  const { getMaterialHotkey, getDisplayKey } = useHotkeyContext();

  useDisableHotkeyScopes(EDIT_EVENT_SUB_MODAL_DISABLED_SCOPES, open);

  const selectableMaterials = useMemo(
    () =>
      excludeMaterialName != null
        ? filteredMaterials.filter(
            (material) => material.name !== excludeMaterialName
          )
        : filteredMaterials,
    [filteredMaterials, excludeMaterialName]
  );

  const selectedValue = event[targetField] ?? null;

  const materialHotkeyCodes = useMemo(() => {
    return [
      getMaterialHotkey(-1),
      ...selectableMaterials.map((_, index) => getMaterialHotkey(index)),
    ];
  }, [selectableMaterials, getMaterialHotkey]);

  const materialKeyNames = useMemo(
    () => materialHotkeyCodes.map(keyCodeToKeyName),
    [materialHotkeyCodes]
  );

  const handleMaterialSelect = (material: string | undefined) => {
    onChange(
      targetField === "material"
        ? { material: material ?? null, coMaterial: null }
        : { material: event.material, coMaterial: material ?? null }
    );
    onClose();
  };

  useHotkeys(
    materialKeyNames,
    (keyboardEvent) => {
      const hotkeyIndex = materialHotkeyCodes.findIndex(
        (hotkey) => hotkey === keyboardEvent.code
      );
      const fixedIndex = hotkeyIndex - 1;
      if (fixedIndex === -1) {
        handleMaterialSelect(undefined);
        return;
      }
      if (fixedIndex >= 0 && fixedIndex < selectableMaterials.length) {
        const material = selectableMaterials[fixedIndex];
        if (material) {
          handleMaterialSelect(material.name);
        }
      }
    },
    {
      enabled: open,
      enableOnFormTags: false,
      preventDefault: true,
    },
    [materialKeyNames, materialHotkeyCodes, selectableMaterials, open]
  );

  useHotkeys(
    "escape",
    () => {
      onClose();
    },
    {
      enabled: open,
      enableOnFormTags: false,
      preventDefault: true,
    },
    [open, onClose]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {targetField === "coMaterial"
          ? t("selectCoMaterial")
          : t("selectMaterial")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 1 }}>
          <MaterialChip
            material={null}
            selected={!selectedValue}
            onClick={() => {
              handleMaterialSelect(undefined);
            }}
            shortcut={getDisplayKey("unknownMaterial")}
            nullLabel={targetField === "coMaterial" ? t("none") : undefined}
          />
          {selectableMaterials.map((material, index) => (
            <MaterialChip
              key={material.id}
              material={material}
              selected={selectedValue === material.name}
              onClick={() => {
                handleMaterialSelect(material.name);
              }}
              shortcut={getDisplayKey(`material_${index}`)}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
